<script setup lang="ts">
import { VueQueryDevtools } from "@tanstack/vue-query-devtools";
import { onErrorCaptured } from "vue";
import { useRouter } from "vue-router";

import { DATA_TEST_IDS } from "@/constants/data-test-ids.ts";
import { NoApplicationRightsError } from "@/lib/errors/auth/no-application-rights-error";
import { SessionExpiredError } from "@/lib/session-expired-error";
import { useTypedIntl } from "@/locales/intl";
import { ERouteName } from "@/routeName.ts";
import { useAuthenticationStore } from "@/stores/authentication/authentication-store.ts";
import { useInterfaceStore } from "@/stores/interface/interface-store.ts";

import ConfirmDialog from "primevue/confirmdialog";
import Toast from "primevue/toast";
import { useToast } from "primevue/usetoast";

import StompProvider from "@/components/realtime/stomp-provider.vue";
import SettingsModal from "@/components/settings/settings-modal.vue";
import SubscriberTransferSignaturePromptHost from "@/components/subscriber/subscriber-transfer-signature-prompt-host.vue";
import ThemeProvider from "@/components/theme/theme-provider.vue";
import ToastProvider from "@/components/toast/toast-provider.vue";

// 3 sec
const ERROR_DIALOG_LIFETIME = 3000;

const router = useRouter();
const toast = useToast();
const { t, k } = useTypedIntl();
const auth = useAuthenticationStore();
const interfaceStore = useInterfaceStore();

function onSessionExpired(): void {
  toast.add({
    severity: "error",
    summary: t(k.toast.login.sessionExpired),
    life: ERROR_DIALOG_LIFETIME,
    closable: true,
  });
  auth.logout();
  router.push({ name: ERouteName.LOGIN });
}

function onInsufficientRights(): void {
  toast.add({
    severity: "error",
    summary: t(k.toast.login.noApplicationRights),
    life: ERROR_DIALOG_LIFETIME,
    closable: true,
  });
  auth.logout();
  router.push({ name: ERouteName.LOGIN });
}

onErrorCaptured((error) => {
  if (error instanceof SessionExpiredError) {
    onSessionExpired();
    return false;
  }

  if (error instanceof NoApplicationRightsError) {
    onInsufficientRights();
    return false;
  }
});
</script>

<template>
  <ThemeProvider>
    <StompProvider :enabled="auth.loggedIn">
      <ToastProvider>
        <router-view />

        <SubscriberTransferSignaturePromptHost />
      </ToastProvider>
    </StompProvider>
  </ThemeProvider>
  <VueQueryDevtools />

  <SettingsModal v-model:visible="interfaceStore.settingsModalVisible" />

  <ConfirmDialog
    class="max-w-[45rem]"
    :pt="{
      root: {
        'data-test-id': DATA_TEST_IDS.components.confirmDialog.component,
      },
      acceptButton: {
        root: {
          'data-test-id': DATA_TEST_IDS.components.confirmDialog.acceptButton,
        },
      },
      rejectButton: {
        root: {
          'data-test-id': DATA_TEST_IDS.components.confirmDialog.rejectButton,
        },
      },
    }"
  />

  <Toast
    position="top-center"
    :pt="{
      message: {
        'data-test-id': DATA_TEST_IDS.components.toast.message(),
      },
    }"
  />
</template>

<style scoped></style>
